import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export const ActionType = {
  TOOL_ADD: 'TOOL_ADD',
  TOOL_DELETE: 'TOOL_DELETE',
  TOOL_MOVE: 'TOOL_MOVE',
  TOOL_UPDATE: 'TOOL_UPDATE',
  GROUP_ADD: 'GROUP_ADD',
  GROUP_DELETE: 'GROUP_DELETE',
  PLACED_GROUP_ADD: 'PLACED_GROUP_ADD',
  PLACED_GROUP_DELETE: 'PLACED_GROUP_DELETE',
  PLACED_GROUP_MOVE: 'PLACED_GROUP_MOVE',
  TAB_OPERATION: 'TAB_OPERATION',
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

export interface StateSnapshot {
  timestamp: string;
  actionType: ActionType;
  description: string;
  // Snapshot of the timeline state at the time of the action
  timelineSnapshot: {
    tools: unknown[];
    tracks: unknown[];
    groups: unknown[];
    placedGroups: unknown[];
    duration: number;
    playheadPosition: number;
  };
  // Snapshot of scene state for tab operations
  sceneSnapshot?: {
    tabs: unknown[];
    activeTabId: string | null;
  };
}

interface TabHistory {
  undoStack: StateSnapshot[];
  redoStack: StateSnapshot[];
}

interface HistoryState {
  histories: Record<string, TabHistory>;
}

interface HistoryActions {
  recordAction: (tabId: string, snapshot: StateSnapshot) => void;
  undo: (tabId: string) => StateSnapshot | null;
  redo: (tabId: string) => StateSnapshot | null;
  clearHistory: (tabId: string) => void;
  getHistoryForTab: (tabId: string) => TabHistory;
}

type HistoryStore = HistoryState & HistoryActions;

const MAX_UNDO_STACK_SIZE = 5;

const createEmptyTabHistory = (): TabHistory => ({
  undoStack: [],
  redoStack: [],
});

export const useHistoryStore = create<HistoryStore>()(
  immer((set, get) => ({
    // Initial state
    histories: {},

    // Actions
    recordAction: (tabId: string, snapshot: StateSnapshot) =>
      set((state) => {
        // Initialize history for tab if it doesn't exist
        if (!state.histories[tabId]) {
          state.histories[tabId] = createEmptyTabHistory();
        }

        const tabHistory = state.histories[tabId];

        // Add snapshot to undo stack
        tabHistory.undoStack.push(snapshot);

        // Enforce max stack size (keep only the last 5 actions)
        if (tabHistory.undoStack.length > MAX_UNDO_STACK_SIZE) {
          tabHistory.undoStack.shift();
        }

        // Clear redo stack when a new action is recorded
        tabHistory.redoStack = [];
      }),

    undo: (tabId: string) => {
      const state = get();
      const tabHistory = state.histories[tabId];

      if (!tabHistory || tabHistory.undoStack.length === 0) {
        return null;
      }

      // Get the previous state to restore (the one before current)
      const previousSnapshot =
        tabHistory.undoStack.length > 1
          ? tabHistory.undoStack[tabHistory.undoStack.length - 2]
          : null;

      set((state) => {
        const tabHistory = state.histories[tabId];

        // Move current snapshot from undo stack to redo stack
        const poppedSnapshot = tabHistory.undoStack.pop();
        if (poppedSnapshot) {
          tabHistory.redoStack.push(poppedSnapshot);

          // Enforce max redo stack size
          if (tabHistory.redoStack.length > MAX_UNDO_STACK_SIZE) {
            tabHistory.redoStack.shift();
          }
        }
      });

      // Return the previous state to restore to
      return previousSnapshot;
    },

    redo: (tabId: string) => {
      const state = get();
      const tabHistory = state.histories[tabId];

      if (!tabHistory || tabHistory.redoStack.length === 0) {
        return null;
      }

      // Get the snapshot from redo stack (the state to restore)
      const snapshotToRestore =
        tabHistory.redoStack[tabHistory.redoStack.length - 1];

      set((state) => {
        const tabHistory = state.histories[tabId];

        // Move snapshot from redo stack to undo stack
        const poppedSnapshot = tabHistory.redoStack.pop();
        if (poppedSnapshot) {
          tabHistory.undoStack.push(poppedSnapshot);

          // Enforce max undo stack size
          if (tabHistory.undoStack.length > MAX_UNDO_STACK_SIZE) {
            tabHistory.undoStack.shift();
          }
        }
      });

      // Return the snapshot that was just moved to undo stack (now top of undo stack)
      return snapshotToRestore;
    },

    clearHistory: (tabId: string) =>
      set((state) => {
        if (state.histories[tabId]) {
          state.histories[tabId] = createEmptyTabHistory();
        }
      }),

    getHistoryForTab: (tabId: string) => {
      const state = get();
      return state.histories[tabId] || createEmptyTabHistory();
    },
  }))
);
